import {
	Client as searchClient,
	TrendingResponseModel,
} from "@searchspring/snap-client";

import { ProductData } from "../../5874/types";
import { getProductsByIds } from "../../bigcommerce/product";
import SearchClient from "../search-client";
import {
	RecommendationsRequest,
	SearchFacet,
	SearchMerchandising,
	SearchMeta,
	SearchRequest,
	SearchResults,
	SearchSortByOptions,
	SearchSpringClientConfig,
	SearchSuggestionResults,
	SearchSuggestionsRequest,
} from "../types";

import { getBigCommerceImageUrls } from "@/lib/bigcommerce/utils/utils";
import {
	AutocompleteResponseModel,
	MetaResponseModel,
	SearchRequestModel,
	SearchResponseModel,
} from "@searchspring/snapi-types";

export class SearchSpringSearchClient extends SearchClient {
	private client: searchClient;

	private sortByOptions: Record<string, { label: string; value: string }> = {
		default: {
			label: "Relevance",
			value: "default",
		},
		name_asc: {
			label: "Name (A-Z)",
			value: "name_asc",
		},
		name_desc: {
			label: "Name (Z-A)",
			value: "name_desc",
		},
	};

	constructor(config: SearchSpringClientConfig) {
		super(config);

		this.client = new searchClient(
			{
				siteId: config.siteId,
			},
			{
				mode:
					(process.env.NEXT_PUBLIC_APP_ENV || "development") === "production"
						? "production"
						: "development",
			},
		);
	}

	public getSortBy() {
		return {
			options: this.sortByOptions,
			default: "default",
		};
	}

	private createSearchMeta(
		request: SearchRequest,
		results: [MetaResponseModel, SearchResponseModel],
	): SearchMeta {
		const [metaResponse, searchResponse] = results;

		const createFacets = (): SearchFacet[] => {
			const requestFilters = searchResponse.facets || [];

			const facetsToReturn: SearchFacet[] = requestFilters
				.map((facet) => {
					if (!facet.field) {
						return undefined;
					}

					const facetIsActive = facet.filtered === true;

					const options = (facet as any).values.map((value: any) => {
						const isActive = facetIsActive && value.filtered === true;

						return {
							label: value.label,
							value: value.value,
							count: value.count,
							isActive: isActive,
							isSelected: isActive,
						};
					});

					return {
						label: facet.field,
						value: facet.field,
						options: options,
						activeCount: options.filter((option: any) => option.isActive)
							.length,
					};
				})
				.filter((facet) => facet !== undefined);

			return facetsToReturn;
		};

		const totalResults = searchResponse.pagination?.totalResults || 0;
		const hitsPerPage = searchResponse.pagination?.pageSize || 0;
		const page = (searchResponse.pagination?.page || 1) - 1; // searchspring uses 1-based indexing
		const total = searchResponse.pagination?.totalPages || 0;

		const pagination = {
			page,
			total,
		};

		const itemCount = {
			from: hitsPerPage * page + 1,
			to: Math.min(hitsPerPage * (page + 1), totalResults),
			total: totalResults,
		};

		return {
			pagination: pagination,
			count: itemCount,
			facets: createFacets(),
			sortBy: this.createSortBy(request, this.sortByOptions),
		} as SearchMeta;
	}

	private createMerchandising(
		request: SearchRequest,
		results: [MetaResponseModel, SearchResponseModel],
	): SearchMerchandising {
		return {
			banners: [],
			redirect: undefined,
		};
		// const renderingContent = results[1].renderingContent;

		// const banners = renderingContent?.widgets?.banners?.map((banner) => {
		// 	return {
		// 		image: banner.image.urls?.[0].url,
		// 		link: {
		// 			url: banner.link?.url,
		// 			target: banner.link?.target,
		// 		},
		// 	} as SearchMerchandisingBanner;
		// });

		// const redirect = (renderingContent as any)?.redirect?.url || undefined;

		// return {
		// 	banners: banners ?? [],
		// 	redirect: redirect,
		// };
	}

	private async parseResults(
		request: SearchRequest,
		results: [MetaResponseModel, SearchResponseModel],
	): Promise<SearchResults> {
		const [metaResponse, searchResponse] = results;

		const initialProducts = searchResponse.results?.map((result: any) => {
			const url = new URL(result.mappings.core.url);
			const path = url.pathname;

			return {
				id: result.mappings.core.uid,
				graphqlId: "",
				productId: Number(result.mappings.core.uid),
				path: path,
				sku: result.mappings.core.sku,
				name: result.mappings.core.name,
				description: "",
				warranty: "",
				brand: {
					name: result.mappings.core.brand,
					path: "",
				},
				isNotPurchasable: false,
				media: {
					images: [
						{
							isThumbnail: true,
							url: getBigCommerceImageUrls(
								result.mappings.core.thumbnailImageUrl,
							),
						},
					],
				},
				inventory: {
					isInStock: true,
				},
				// productClass: result.custom_productclass,
				//customFields: result.customFields,
				//variants: result.variants,
				//seo: result.seo,
				//mpn: result.mpn,
				//brand: result.brand,
			} as ProductData;
		});

		const meta = this.createSearchMeta(request, results);
		const merchandising = this.createMerchandising(request, results);

		// covert it to a plain object
		const raw = JSON.parse(JSON.stringify(results));

		return {
			products: initialProducts,
			meta: meta,
			merchandising: merchandising,
			suggestedCategoryIds: await this.getSuggestedCategories(request, results),
			tracking: {},
			raw,
		} as SearchResults;
	}

	private async getEnrichedProducts(
		results: [MetaResponseModel, SearchResponseModel],
	): Promise<ProductData[]> {
		const [metaResponse, searchResponse] = results;

		const entityIds = (searchResponse.results ?? []).map((result: any) =>
			Number(result.mappings.core.uid),
		);

		if (entityIds.length === 0) {
			return [];
		}

		const products = await getProductsByIds(entityIds);

		// sort the order so they match the algolia results order.
		// Index the positions first: the previous `entityIds.findIndex` inside
		// the comparator was O(n^2 log n) across the sort.
		const order = new Map(entityIds.map((entityId, index) => [entityId, index]));

		products.sort(
			(a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0),
		);

		return products;
	}

	private async getSuggestedCategories(
		request: SearchRequest,
		results: [MetaResponseModel, SearchResponseModel],
	): Promise<any> {
		const [metaResponse, searchResponse] = results;

		// get catagory ids that are being used as a filter
		const filteredCategoriesIds = (request.filters?.categories_ids ?? []).map(
			(filter) => filter.value,
		);

		// get all category ids from facets, removing the ones used as a filter
		const categoriesIds = Object.keys(
			(searchResponse.facets as any).categories_ids ?? {},
		).filter((id) => !filteredCategoriesIds.includes(id));

		if (!categoriesIds.length) {
			return [];
		}

		const top5Categories = categoriesIds.slice(0, 5);

		return top5Categories;
	}

	private async performSearch(request: SearchRequest): Promise<SearchResults> {
		const filters = Object.entries(request.filters || {}).flatMap(
			([field, filters]) => {
				return filters.map((filter) => {
					let _field = field;

					if (_field === "categories_ids") {
						_field = "category_ids";
					}

					return {
						type: "value",
						field: _field,
						background: filter.isBackgroundFilter ?? false,
						value: filter.value,
					};
				});
			},
		);

		const searchParams = {
			pagination: {
				page: (request.page ?? 1) - 1,
				pageSize: request.perPage ?? 36,
			},
			search: {
				query: {
					string: request.query ?? "",
					spellCorrection: false,
				},
			},
			filters,
		} as SearchRequestModel;

		const results = await this.client.search(searchParams);

		return this.parseResults(request, results);
	}

	async search(
		request: SearchRequest,
	): Promise<[SearchResults, () => Promise<ProductData[]>]> {
		const results = await this.performSearch(request);

		const getProducts = () => {
			return this.getEnrichedProducts(results.raw);
		};

		return [results, getProducts];
	}

	async getSearchSuggestions(request: SearchSuggestionsRequest): Promise<any> {
		const limit = (request.perPage ?? 5) + 1;

		let results;

		const useTrending = !request.query;

		if (useTrending) {
			results = await this.client.trending({
				limit,
			});
		} else {
			const [metaResponse, autocompleteResponse] =
				await this.client.autocomplete({
					search: {
						query: {
							string: request.query,
							spellCorrection: true,
						},
					},
					pagination: {
						pageSize: limit,
					},
				});

			results = autocompleteResponse;
		}

		try {
			let suggestions = [];

			if (useTrending) {
				suggestions = (results as TrendingResponseModel).trending.queries.map(
					(query) => query.searchQuery,
				);
			} else {
				suggestions =
					(
						results as AutocompleteResponseModel
					).autocomplete?.alternatives?.map(
						(alternative) => alternative.text,
					) || [];
			}

			const queryIndex = suggestions.indexOf(request.query);

			// if no suggetions match the query, remove the extra suggestion
			if (queryIndex === -1) {
				suggestions.pop();
			} else {
				// remove the query from the suggestions
				suggestions = suggestions.filter((_, index) => index !== queryIndex);
			}

			return {
				query: request.query,
				suggestions: suggestions,
				raw: {},
			} as SearchSuggestionResults;
		} catch (error) {
			console.error("SearchSpringSearchClient.getSearchSuggestions", error);
			return [];
		}
	}

	async getRecommendations(
		request: RecommendationsRequest,
	): Promise<[any, () => Promise<ProductData[]>]> {
		return [{}, () => Promise.resolve([])];
	}
}
